import {
  amplitudeSendEvent,
  GTM_NOTSET_INTEGER,
  GTM_NOTSET_STRING,
  getGtmAttr,
  getScreenName,
} from '@musinsa/frontend-utils';
import { Button, IcArrowDown, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { VisitorCurationGoodsItem } from '@musinsa/services-curator';
import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { VisitorCurationProduct } from '../../product/components/variants/VisitorCurationProduct';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface Props {
  snsTitle: string;
  title: string;
  curationId: string;
  items: VisitorCurationGoodsItem[];
  pageCache: Map<string, number>;
  setPageCache: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  curationIndex: number;
  defaultPage?: number;
}

const MIN_COUNT = 6;

const toLogProduct = (item: VisitorCurationGoodsItem) => ({
  product_id: item.goodsNo,
  product_name: item.goodsName,
  price: item.price,
  discount_rate: item.salesRate,
  brand_name: item.brandName,
});

export const GoodItems = ({
  snsTitle,
  title,
  curationId,
  items,
  pageCache,
  setPageCache,
  curationIndex,
  defaultPage = 1,
}: Props) => {
  const params = useParams();
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const is29CM = service === SERVICE['29CM'];
  const curatorId = snsTitle || (params.shopSlug as string);

  // 캐시에서 해당 curationId의 페이지를 가져오거나 기본값 사용
  const currentPage = pageCache.get(curationId) || defaultPage;

  // 더보기 버튼 표시 여부 계산
  const showMore = useMemo(
    () => items.length > MIN_COUNT * currentPage,
    [items.length, currentPage],
  );

  // 페이지 증가 핸들러 - 캐시에 저장
  const onLoadMore = () => {
    if (isMusinsa) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_more',
        component_name: 'more_button',
        event_name: 'click_more_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curationfeed',
        type: 'goods',
      });
    }
    if (is29CM) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_shop',
          button_id: 'more',
          button_name: '더보기',
          section_name: 'CuratorShop',
          section_title: 'CuratorShop',
          position: curationIndex + 1,
          reward_program_key: 'curator_shop',
          reward_key: `curator_${curatorId}`,
        },
      });
    }

    const nextPage = currentPage + 1;
    setPageCache((prev) => new Map(prev).set(curationId, nextPage));
  };

  return (
    <div>
      <div className="grid grid-cols-3 w-full bg-neutral-color-white">
        {[...items].slice(0, MIN_COUNT * currentPage).map((item, index) => {
          const gtmAttr = isMusinsa
            ? getGtmAttr(['select_item', 'view_item_list'], {
                discount:
                  item.normalPrice != null && item.price != null
                    ? item.normalPrice - item.price
                    : GTM_NOTSET_INTEGER,
                discount_rate: item.salesRate?.toString() ?? GTM_NOTSET_STRING,
                item_brand: item.brandName ?? GTM_NOTSET_STRING,
                item_category: GTM_NOTSET_STRING,
                item_id: String(item.goodsNo),
                item_list_id: GTM_NOTSET_STRING,
                item_list_index: String(index),
                original_price: item.normalPrice ?? GTM_NOTSET_INTEGER,
                price: item.price ?? GTM_NOTSET_INTEGER,
                quantity: 1,
                ...(item.couponPrice != null ? { best_price: item.couponPrice } : {}),
              })
            : undefined;

          return (
            <VisitorCurationProduct
              enableDeeplink
              key={item.goodsNo}
              item={item}
              index={index}
              {...gtmAttr}
              callbackClick={() => {
                if (isMusinsa) {
                  amplitudeSendEvent({
                    action_name: 'click',
                    component_name: 'product',
                    event_name: 'click_product',
                    from: getScreenName(),
                    id: curatorId,
                    index,
                    section_name: 'curator_curationfeed',
                    type: 'goods',
                    ...toLogProduct(item),
                  });
                }
                if (is29CM) {
                  track({
                    schema: SNOWPLOW_SCHEME.CLICK_ITEM,
                    data: {
                      page_name: 'curator_shop',
                      section_name: `curation-${curationId}`,
                      section_title: title,
                      position: curationIndex + 1,
                      item_position: index + 1,
                      item_no: Number(item.goodsNo),
                      item_name: item.goodsName,
                      brand_no: item.brandNo,
                      brand_name: item.brandName,
                      large_category_no: item.largeCategoryNo ?? -1,
                      large_category_name: item.largeCategoryName ?? '',
                      middle_category_no: item.middleCategoryNo ?? -1,
                      middle_category_name: item.middleCategoryName ?? '',
                      small_category_no: item.smallCategoryNo ?? -1,
                      small_category_name: item.smallCategoryName ?? '',
                      selection_id: `curator_${curatorId}`,
                    },
                  });
                }
              }}
              callbackImpression={() => {
                if (is29CM) {
                  track({
                    schema: SNOWPLOW_SCHEME.IMPRESSION_ITEM,
                    data: {
                      page_name: 'curator_shop',
                      section_name: `curation-${curationId}`,
                      section_title: title,
                      position: curationIndex + 1,
                      item_position: index + 1,
                      item_no: Number(item.goodsNo),
                      item_name: item.goodsName,
                      brand_no: item.brandNo,
                      brand_name: item.brandName,
                      large_category_no: item.largeCategoryNo ?? -1,
                      large_category_name: item.largeCategoryName ?? '',
                      middle_category_no: item.middleCategoryNo ?? -1,
                      middle_category_name: item.middleCategoryName ?? '',
                      small_category_no: item.smallCategoryNo ?? -1,
                      small_category_name: item.smallCategoryName ?? '',
                      selection_id: `curator_${curatorId}`,
                    },
                  });
                }
              }}
              onClickLikeButton={(_item, isLiked) => {
                if (isMusinsa) {
                  amplitudeSendEvent({
                    action_name: 'click',
                    component_name: 'product',
                    event_name: isLiked ? 'like_product_product' : 'unlike_product_product',
                    from: getScreenName(),
                    id: curatorId,
                    index,
                    section_name: 'curator_curationfeed',
                    type: 'goods',
                    ...toLogProduct(item),
                  });
                }
                if (is29CM) {
                  track({
                    schema: SNOWPLOW_SCHEME.LIKE_ITEM,
                    data: {
                      page_name: 'curator_shop',
                      section_name: `curation-${curationId}`,
                      section_title: title,
                      position: curationIndex + 1,
                      item_position: index + 1,
                      item_no: Number(item.goodsNo),
                      item_name: item.goodsName,
                    },
                  });
                }
              }}
            />
          );
        })}
      </div>
      {showMore && (
        <div className="flex justify-center px-4 py-3">
          <Button
            size={40}
            variant="tertiaryOutlinedGray"
            className="flex items-center gap-[2px] w-full"
            onClick={onLoadMore}
          >
            <Typography variant="body14pxMed">
              더보기({MIN_COUNT * currentPage}/{items.length}개)
            </Typography>
            <IcArrowDown width="16px" height="16px" />
          </Button>
        </div>
      )}
    </div>
  );
};
